<template>
  <div class="add-stop-view-edit">
    <AddStopEdit
      v-if="isInEditMode"
      :id="id"
      :location="location"
      :stop-type="stopType"
      :stop-selector="stopSelector"
      :append-to="appendTo"
      :requires-confirmation="requiresConfirmation"
      @cancel="emit('cancel')"
      @confirm="emit('confirm', $event)"
    />
    <AddStopView
      v-else-if="renderableLocation && stopType"
      :id="id"
      :location="renderableLocation"
      :stop-type="stopType"
      :removable="removable"
      @remove="emit('remove')"
    />
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';

import { Location } from '@rygen/rygen-ui/model';

import { EmbeddableLocation, ShipmentStopType } from '@/model';
import { AddStopPayload, StopSelector } from '@/model/routeBuilder';

import AddStopEdit from '@/components/routeBuilder/addStopToShipment/AddStopEdit.vue';
import AddStopView from '@/components/routeBuilder/addStopToShipment/AddStopView.vue';

const isInEditMode = defineModel('editMode', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  location: {
    type: Object as PropType<Location>,
    default: () => undefined,
  },
  stopType: {
    type: String as PropType<ShipmentStopType>,
    default: () => undefined,
  },
  stopSelector: {
    type: String as PropType<StopSelector>,
    default: () => 'intermediate',
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  requiresConfirmation: {
    type: Boolean,
    default: () => false,
  },
  removable: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  location,
  stopType,
  stopSelector,
  appendTo,
  requiresConfirmation,
  removable,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'confirm', payload: AddStopPayload): void;
  (event: 'remove'): void;
}>();

const renderableLocation = computed(() => {
  return location?.value
    ? EmbeddableLocation.fromLocation(location.value)
    : undefined;
});
</script>

<style>
.add-stop-view-edit {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border: none;

  div& {
    gap: var(--sp-1);
  }

  form& {
    gap: var(--gap-1);
  }

  fieldset& {
    gap: var(--gap-1);
  }
}
</style>
